/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a drug shipment.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDrugShipmentState {
    drugShipment: Dtos.DrugShipment | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    saveState: IRequestState<Dtos.ResponseStatus>;
    setStatusState: IRequestState<Dtos.ResponseStatus>;
    deleteState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDrugShipmentState {
    drugShipment: IInitialDrugShipmentState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentInitialState: IInitialDrugShipmentState = {
    drugShipment: null,
    loadState: {
        state: RequestState.None
    },
    saveState: {
        state: RequestState.None
    },
    setStatusState: {
        state: RequestState.None
    },
    deleteState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDrugShipmentSelector: TypedUseSelectorHook<IDrugShipmentState> = useSelector;

export const DrugShipmentSelectors = {
    selectDrugShipment: (state: IDrugShipmentState) => state.drugShipment.drugShipment,
    selectLoadState: (state: IDrugShipmentState) => state.drugShipment.loadState,
    selectSaveState: (state: IDrugShipmentState) => state.drugShipment.saveState,
    selectSetStatusState: (state: IDrugShipmentState) => state.drugShipment.setStatusState,
    selectDeleteState: (state: IDrugShipmentState) => state.drugShipment.deleteState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DrugShipmentReducer extends ImmerReducer<IInitialDrugShipmentState> {
    public loadById(id: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(drugShipment?: Dtos.DrugShipment) {
        this.draftState.drugShipment = drugShipment ? drugShipment : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugShipment = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public save(form: Dtos.DrugShipment) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public saveSuccess(drugShipment?: Dtos.DrugShipment) {
        this.draftState.drugShipment = drugShipment ? drugShipment : null;
        this.draftState.saveState = {
            state: RequestState.Success
        };
    }

    public saveFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugShipment = null;
        this.draftState.saveState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public setStatus(form: Dtos.DrugShipment) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public setStatusSuccess(drugShipment?: Dtos.DrugShipment) {
        this.draftState.drugShipment = drugShipment ? drugShipment : null;
        this.draftState.setStatusState = {
            state: RequestState.Success
        };
    }

    public setStatusFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugShipment = null;
        this.draftState.setStatusState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public deleteById(id: number) {
        this.draftState.deleteState = {
            state: RequestState.Pending
        };
    }

    public deleteSuccess(drugShipment?: Dtos.DrugShipment) {
        this.draftState.drugShipment = null;
        this.draftState.deleteState = {
            state: RequestState.Success
        };
    }

    public deleteFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.deleteState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DrugShipmentInitialState };
    }
};

export const DrugShipmentActions = createActionCreators(DrugShipmentReducer);
const reducer = createReducerFunction(DrugShipmentReducer, DrugShipmentInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DrugShipmentApi = {
    loadById: (id: number) => {
        return client.get(new Dtos.GetDrugShipmentById({
            id
        }));
    },
    save: (drugShipment?: Dtos.DrugShipment) => {
        return client.post(new Dtos.SetDrugShipment({
            drugShipment
        }));
    },
    setStatus: (drugShipment?: Dtos.DrugShipment) => {
        return client.post(new Dtos.SetDrugShipmentStatus({
            drugShipment
        }));
    },
    deleteById: (id: number) => {
        return client.post(new Dtos.DeleteDrugShipment({
            drugShipmentId: id
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentLogic = {
    loadById: createLogic<IDrugShipmentState, {}, undefined, string, ReturnType<typeof DrugShipmentActions.loadById>>({
        type: DrugShipmentActions.loadById.type,
        process: async ({ action }, dispatch, done) => {
            const id = action.payload;

            try {
                const response = await DrugShipmentApi.loadById(id);

                dispatch(DrugShipmentActions.loadSuccess(
                    response.drugShipment
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    save: createLogic<IDrugShipmentState, {}, undefined, string, ReturnType<typeof DrugShipmentActions.save>>({
        type: DrugShipmentActions.save.type,
        process: async ({ action }, dispatch, done) => {
            const drugShipment = action.payload;

            try {
                const response = await DrugShipmentApi.save(drugShipment);

                dispatch(DrugShipmentActions.saveSuccess(
                    response.drugShipment
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentActions.saveFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    setStatus: createLogic<IDrugShipmentState, {}, undefined, string, ReturnType<typeof DrugShipmentActions.setStatus>>({
        type: DrugShipmentActions.setStatus.type,
        process: async ({ action }, dispatch, done) => {
            const drugShipment = action.payload;

            try {
                const response = await DrugShipmentApi.setStatus(drugShipment);

                dispatch(DrugShipmentActions.setStatusSuccess(
                    response.drugShipment
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentActions.setStatusFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    deleteById: createLogic<IDrugShipmentState, {}, undefined, string, ReturnType<typeof DrugShipmentActions.deleteById>>({
        type: DrugShipmentActions.deleteById.type,
        process: async ({ action }, dispatch, done) => {
            const id = action.payload;

            try {
                await DrugShipmentApi.deleteById(id);

                dispatch(DrugShipmentActions.deleteSuccess(
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentActions.deleteFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DrugShipmentLogic.loadById,
    DrugShipmentLogic.save,
    DrugShipmentLogic.setStatus,
    DrugShipmentLogic.deleteById
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDrugShipment = (): [Dtos.DrugShipment | null,
    IRequestState<Dtos.ResponseStatus>,
    IRequestState<Dtos.ResponseStatus>,
    IRequestState<Dtos.ResponseStatus>,
    IRequestState<Dtos.ResponseStatus>,
    typeof DrugShipmentActions] => {
    const drugShipment = useSelector(DrugShipmentSelectors.selectDrugShipment);
    const loadState = useSelector(DrugShipmentSelectors.selectLoadState);
    const saveState = useSelector(DrugShipmentSelectors.selectSaveState);
    const setStatusState = useSelector(DrugShipmentSelectors.selectSetStatusState);
    const deleteState = useSelector(DrugShipmentSelectors.selectDeleteState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DrugShipmentActions>(() => {
        return bindActionCreators(DrugShipmentActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DrugShipmentActions, dispatch));
    }, [dispatch, setActions]);

    return [
        drugShipment,
        loadState,
        saveState,
        setStatusState,
        deleteState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('drugShipment', reducer as any, logics as any);